<template>
  <div class="protocol-sign-page" :class="{ 'is-embedded': embedded }">
    <!-- 标题条：返回按钮+标题布局参考 certAuditManageDetail 的 Title 组件 -->
    <div v-if="!embedded" class="title-box">
      <!-- FOUR-2975 复测终版：恒显示返回——待办直达（无浏览历史）时 goBack 兜底跳认证资质申请页，
           与站内"申请页去签署→点返回"同效 -->
      <div class="page-back" @click="goBack">返回</div>
      <div class="page-title">平台保密协议</div>
    </div>

    <div class="protocol-sign">
      <!-- 协议内容：历史申请只展示当次留档正文，不用当前配置冒充（新申请统一在认证页内签署） -->
      <div v-if="!embedded" class="base">
        <div class="title" @click="isExpandContent = !isExpandContent">
          <div class="title-config">
            <i :class="isExpandContent ? 'el-icon-caret-bottom' : 'el-icon-caret-right'"></i>
            协议内容
          </div>
        </div>
        <div v-show="isExpandContent" class="protocol-content">
          <template v-if="isHistoryView">
            <div v-if="signatureOwnerChanged" class="blocked-tip">当前人员或企业已变化，请重新进入页面</div>
            <div v-else-if="historyLoading" class="blocked-tip">正在查询历史签署信息</div>
            <div v-else-if="historyError" class="blocked-tip">
              历史签署信息查询失败
              <el-button size="mini" @click="loadHistorySignature">重试</el-button>
            </div>
            <template v-else-if="historySignature">
              <div v-if="historyHasFrozenContent" v-html="historySignature.protocolContent"></div>
              <div v-else class="blocked-tip">历史申请未保存当次正文，无法展示签署时的协议全文。</div>
            </template>
            <div v-else class="blocked-tip">历史申请未保存当次签名。</div>
          </template>
          <template v-else-if="isViewOther">
            <div class="blocked-tip">历史申请未保存当次正文，无法展示签署时的协议全文。</div>
          </template>
          <div v-else-if="protocol.protocolContent" v-html="protocol.protocolContent"></div>
          <div v-else-if="contextError" class="blocked-tip">
            平台保密协议内容查询失败
            <el-button size="mini" @click="loadProtocolContext">重试</el-button>
          </div>
          <div v-else class="blocked-tip">正在查询平台保密协议</div>
        </div>
      </div>

      <!-- 签署信息 -->
      <div class="base">
        <div v-if="!embedded" class="title" @click="isExpandSign = !isExpandSign">
          <div class="title-config">
            <i :class="isExpandSign ? 'el-icon-caret-bottom' : 'el-icon-caret-right'"></i>
            签署信息
          </div>
        </div>
        <div v-show="embedded || isExpandSign" class="sign-body">
          <!-- 历史申请只读展示该申请快照，不读取、不显示当前签名 -->
          <template v-if="isHistoryView">
            <div v-if="signatureOwnerChanged" class="blocked-tip">当前人员或企业已变化，请重新进入页面</div>
            <div v-else-if="historyLoading" class="blocked-tip">正在查询历史签署信息</div>
            <div v-else-if="historyError" class="blocked-tip">
              历史签署信息查询失败
              <el-button size="mini" @click="loadHistorySignature">重试</el-button>
            </div>
            <template v-else-if="historySignature">
              <div v-if="!historyHasFrozenContent" class="blocked-tip">
                历史申请未保存当次正文，以下为历史签署记录。
              </div>
              <div class="signed-tip">
                已于 {{ formatTime(historySignature.signTime) }} 签署平台保密协议。
              </div>
              <div v-if="historyAttachPath" class="signed-sign-box">
                <div class="sign-label">签名：</div>
                <!-- 白底预览弹窗替代 el-image 默认黑色遮罩查看器（仅本页生效） -->
                <el-image
                  :src="historyAttachPath"
                  class="signed-sign-img sign-viewable"
                  @click="signPreviewVisible = true"
                ></el-image>
              </div>
            </template>
            <div v-else class="blocked-tip">历史申请未保存当次签名。</div>
          </template>
          <!-- 仅带 staffGuid 的旧地址没有申请快照，不能拿当前签名冒充历史 -->
          <template v-else-if="!embedded && isViewOther">
            <div class="blocked-tip">历史申请未保存当次签名，请从认证审核详情查看。</div>
          </template>
          <template v-else>
            <div v-if="embedded && !signaturePresent" class="protocol-agreement">
              <el-checkbox v-model="agreed" :disabled="!canSign">我已阅读并同意<span class="protocol-link" @click.stop.prevent="openProtocol">《平台保密协议》</span></el-checkbox>
            </div>
            <!-- 已有本申请签名：展示签名/沿用信息，可修改后回填表单 -->
            <template v-if="signaturePresent && !editingSignature">
              <div class="signed-tip">
                <template v-if="reuseSignature">
                  已于 {{ formatTime(displaySignTime) }} 签署本协议，本次变更沿用该签名。
                </template>
                <template v-else>已于 {{ formatTime(displaySignTime) }} 签署本协议。</template>
              </div>
              <div v-if="isLegacyDisplay" class="blocked-tip">
                历史签署未保存当次协议正文与摘要，如需提交请重新签署。
              </div>
              <div v-if="displayAttachPath" class="signed-sign-box">
                <div class="sign-label">我的签名：</div>
                <el-image
                  :src="displayAttachPath"
                  class="signed-sign-img sign-viewable"
                  @click="signPreviewVisible = true"
                ></el-image>
              </div>
              <div v-if="contextError && canEdit" class="blocked-tip">
                平台保密协议查询失败，暂时无法修改签名
                <el-button size="mini" @click="loadProtocolContext">重试</el-button>
              </div>
              <el-button v-if="canModifySignature" size="small" @click="startModifySignature">修改签名</el-button>
            </template>
            <template v-else-if="contextError">
              <div class="blocked-tip">
                平台保密协议查询失败，暂时无法签署
                <el-button size="mini" @click="loadProtocolContext">重试</el-button>
              </div>
            </template>
            <template v-else-if="!contextReady">
              <div v-if="signatureOwnerChanged" class="blocked-tip">当前人员或企业已变化，请重新进入认证页面</div>
              <div v-else class="blocked-tip">正在查询平台保密协议</div>
            </template>
            <template v-else-if="canSign">
              <div v-if="identityChanged" class="blocked-tip">
                姓名或身份证号已修改，原签名不再适用，请重新手写签名。
              </div>
              <div v-else-if="legacyReuseUnavailable" class="blocked-tip">
                原签署为历史记录，未保存当次协议正文与摘要，本次需重新签署。
              </div>
              <div class="sign-label">请手写签名：</div>
              <div class="canvas-wrap">
                <canvas
                  ref="signCanvas"
                  width="600"
                  height="200"
                  @mousedown="startDraw"
                  @mousemove="drawing"
                  @mouseup="endDraw"
                  @mouseleave="endDraw"
                ></canvas>
                <div class="canvas-btns">
                  <el-button size="mini" :disabled="submitting" @click="undoDraw">撤销</el-button>
                  <el-button size="mini" :disabled="submitting" @click="clearCanvas">清除</el-button>
                </div>
              </div>
              <div v-if="!embedded" class="sign-date">签署日期：{{ today }}</div>
            </template>
            <template v-else>
              <div v-if="certificationError" class="blocked-tip">认证状态查询失败，无法签署协议</div>
              <div v-else-if="!certificationReady" class="blocked-tip">正在查询认证状态</div>
              <div v-else class="blocked-tip">当前认证状态不可修改签名</div>
            </template>
          </template>
        </div>
      </div>

      <!-- 按钮区 -->
      <div v-if="!isHistoryView && !isViewOther && (editingSignature || canSign)" class="btn-bar">
        <el-button v-if="editingSignature" size="small" :disabled="submitting" @click="cancelModifySignature">取消修改</el-button>
        <el-button v-if="canSign" size="small" type="primary" :loading="submitting" @click="onSign">
          {{ editingSignature ? '确认修改签名' : '确认签署' }}
        </el-button>
      </div>
    </div>

    <el-dialog :visible.sync="protocolDialogVisible" :fullscreen="protocolFullscreen" append-to-body width="820px" top="8vh" custom-class="protocol-reading-dialog" @opened="startProtocolReading" @close="stopProtocolReading">
      <div slot="title" class="protocol-dialog-title">
        <span>平台保密协议</span>
        <button type="button" class="el-dialog__headerbtn protocol-fullscreen-button" :aria-label="protocolFullscreen ? '退出全屏' : '全屏'" :title="protocolFullscreen ? '退出全屏' : '全屏'" :aria-pressed="protocolFullscreen" @click="protocolFullscreen = !protocolFullscreen">
          <i class="el-dialog__close el-icon" :class="protocolFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'" aria-hidden="true"></i>
        </button>
      </div>
      <div v-if="isHistoryView" class="full-protocol-content">
        <div v-if="historyHasFrozenContent" v-html="historySignature.protocolContent"></div>
        <div v-else class="blocked-tip">历史申请未保存当次正文，无法展示签署时的协议全文。</div>
      </div>
      <div v-else ref="protocolContent" class="full-protocol-content" @scroll="checkProtocolBottom" @load.capture="checkProtocolBottom" v-html="protocol.protocolContent"></div>
      <div v-if="protocolReadRequired" slot="footer">
        <el-button type="primary" size="small" :disabled="!protocolReadReady" @click="protocolDialogVisible = false">{{ protocolReadButtonText }}</el-button>
      </div>
    </el-dialog>

    <!-- 签名白底预览弹窗 -->
    <el-dialog
      title="签名预览"
      :visible.sync="signPreviewVisible"
      append-to-body
      width="820px"
      top="8vh"
      custom-class="sign-preview-dialog"
    >
      <img v-if="previewAttachPath" :src="previewAttachPath" alt="签名" class="sign-preview-img" />
    </el-dialog>
  </div>
</template>

<script>
// 编辑签名前后绑定同一账号、人员和企业，避免切换身份后使用旧签名。
function signatureOwner() {
  try {
    const user = JSON.parse(localStorage.userInfo || "{}");
    let userId = localStorage.userId || "";
    if (userId[0] === '"') userId = JSON.parse(userId);
    if (user.userGuid && userId && user.userGuid !== userId) return null;
    const owner = { userGuid: user.userGuid || userId, staffGuid: user.staffGuid, tenantGuid: localStorage.guid };
    return owner.userGuid && owner.staffGuid && owner.tenantGuid ? owner : null;
  } catch (error) { return null; }
}

function signatureFileName() {
  return `signature-${Date.now()}-${Math.random().toString(36).slice(2, 10)}.png`;
}

function sameSignatureOwner(left, right) {
  return !!left && !!right && left.userGuid === right.userGuid
    && left.staffGuid === right.staffGuid && left.tenantGuid === right.tenantGuid;
}

// 新后端返回 protocolSignature；兼容旧申请只存 protocolSignSnapshot 的读取路径。
function parseCertSignature(detail) {
  if (!detail) return null;
  const direct = detail.protocolSignature;
  if (direct && typeof direct === "object") {
    return direct.signed === true && direct.signAttach ? direct : null;
  }
  if (detail.protocolSignSnapshot != null && detail.protocolSignSnapshot !== "") {
    try {
      const snapshot = JSON.parse(detail.protocolSignSnapshot);
      return snapshot && snapshot.signed === true && snapshot.signAttach ? snapshot : null;
    } catch (error) {
      return { corrupted: true };
    }
  }
  return null;
}

export default {
  name: "platformProtocolSign",
  props: {
    embedded: { type: Boolean, default: false },
    certificationReady: { type: Boolean, default: false },
    certificationError: { type: Boolean, default: false },
    certificationState: { type: String, default: "" },
    certificationReadonly: { type: Boolean, default: true },
    // 表单内的签署：新签对象或沿用对象，随认证表单保存/提交
    protocolSignature: { type: Object, default: null },
    effectiveCertGuid: { type: String, default: "" },
    effectiveProtocolSignature: { type: Object, default: null },
    identityChanged: { type: Boolean, default: false },
  },
  data() {
    return {
      // 协议签署上下文（G1 sign-context）：正文/版本/摘要
      protocol: {},
      contextReady: false,
      contextError: false,
      contextRequestId: 0,
      signatureOwnerChanged: false,
      editingSignature: false,
      agreed: false,
      redirecting: false,
      protocolDialogVisible: false,
      protocolFullscreen: false,
      protocolReadSeconds: 0,
      protocolReadBottom: false,
      protocolReadExempt: false,
      confirmingConsent: false,
      // 签名白底预览弹窗
      signPreviewVisible: false,
      // 历史申请只读快照
      historySignature: null,
      historyLoading: false,
      historyError: false,
      historyRequestId: 0,
      // 区块折叠状态（questionnaireAdd 同款可折叠卡片框架）
      isExpandContent: true,
      isExpandSign: true,
      submitting: false,
      today: this.$moment().format("YYYY-MM-DD"),
      painting: false,
      ctx: null,
      hasDrawn: false,
      // 每笔开始前的画布快照（撤销上一步用）
      undoStack: [],
    };
  },
  computed: {
    protocolReadRequired() {
      const signature = this.displaySignature;
      const currentSigned = signature && !this.isLegacyDisplay
        && signature.protocolGuid === this.protocol.protocolGuid
        && String(signature.protocolVersion || "1") === String(this.protocol.protocolVersion || "1")
        && (signature.contentDigest || signature.protocolContentDigest) === this.protocol.contentDigest;
      return this.canSign && !currentSigned && !this.protocolReadExempt;
    },
    protocolReadReady() {
      return !this.protocolReadRequired || (this.protocolReadSeconds >= 30 && this.protocolReadBottom);
    },
    protocolReadButtonText() {
      if (this.protocolReadSeconds < 30) return `请先阅读（还需 ${30 - this.protocolReadSeconds} 秒）`;
      return this.protocolReadBottom ? "已完成阅读，进入签署" : "请滚动至协议底部";
    },
    reuseSignature() {
      return !!(this.protocolSignature && this.protocolSignature.reuseFromCertGuid);
    },
    // 沿用展示被沿用申请的签名/时间；新签展示本表单签名
    displaySignature() {
      if (this.identityChanged && this.reuseSignature) return null;
      if (this.reuseSignature) return this.effectiveProtocolSignature || null;
      return this.protocolSignature || null;
    },
    displayAttachPath() {
      return this.attachPathOf(this.displaySignature);
    },
    displaySignTime() {
      return this.displaySignature ? this.displaySignature.signTime : null;
    },
    previewAttachPath() {
      return this.isHistoryView ? this.historyAttachPath : this.displayAttachPath;
    },
    // 该申请是否已有可用于展示/提交的签名（身份变化后沿用失效）
    signaturePresent() {
      if (this.signatureOwnerChanged) return false;
      const signature = this.protocolSignature;
      if (!signature) return false;
      if (signature.reuseFromCertGuid) return !this.identityChanged && !!this.effectiveProtocolSignature;
      return !!signature.signAttach;
    },
    isLegacyDisplay() {
      const signature = this.displaySignature;
      if (!signature) return false;
      if (signature.legacy === true) return true;
      // 表单新签对象只带 signAttach/guid/version/digest（无 schema），不能误判为 legacy
      return signature.schema != null && Number(signature.schema) < 2;
    },
    // 变更时原有效签署是 legacy，不能沿用，画布区如实提示需重签
    legacyReuseUnavailable() {
      if (!this.embedded || this.protocolSignature) return false;
      const signature = this.effectiveProtocolSignature;
      if (!signature) return false;
      return signature.legacy === true || (signature.schema != null && Number(signature.schema) < 2);
    },
    // 仅带 certGuid 的历史审核地址按申请快照只读展示；自助地址统一跳认证页
    isHistoryView() {
      return !this.embedded && !!this.$route.query.certGuid;
    },
    // 只读查看他人签署（旧审核详情等入口带 staffGuid 跳入）
    isViewOther() {
      if (this.embedded) return false;
      const target = this.$route.query.staffGuid || "";
      return this.isHistoryView || (!!target && target !== this.selfStaffGuid);
    },
    selfStaffGuid() {
      try {
        return JSON.parse(localStorage.userInfo || "{}").staffGuid || "";
      } catch (e) {
        return "";
      }
    },
    historyHasFrozenContent() {
      return !!(this.historySignature && this.historySignature.protocolContent);
    },
    historyAttachPath() {
      return this.attachPathOf(this.historySignature);
    },
    // 嵌入认证页时：认证可编辑且协议上下文可用才允许改签名
    canEdit() {
      if (!this.embedded) return false;
      if (!this.certificationReady || this.certificationReadonly || this.certificationState === "A") return false;
      if (this.signatureOwnerChanged) return false;
      return true;
    },
    canSign() {
      if (!this.canEdit || !this.contextReady || this.contextError || this.isViewOther) return false;
      return this.editingSignature || !this.signaturePresent;
    },
    canModifySignature() {
      return this.canEdit && this.contextReady && !this.contextError
        && this.signaturePresent && !this.editingSignature;
    },
  },
  watch: {
    protocolFullscreen() {
      this.$nextTick(() => this.checkProtocolBottom());
    },
    protocolSignature() {
      if (this.submitting) return;
      this.editingSignature = false;
      this.hasDrawn = false;
      this.undoStack = [];
    },
    canSign(value) {
      if (value) this.$nextTick(() => this.initCanvas());
    },
  },
  created() {
    // 旧待办仍可使用原地址；自助进入统一跳认证页，带 certGuid 的历史审核地址继续只读。
    if (!this.embedded && !this.isHistoryView && !this.isViewOther) {
      this.redirecting = true;
      const productGuid = this.$route.query.productGuid;
      this.$router.replace({ path: "/certInfoManage", query: productGuid ? { productGuid } : {} });
    }
  },
  mounted() {
    if (this.redirecting) return;
    if (this.isHistoryView) {
      this.loadHistorySignature();
      return;
    }
    if (this.embedded) this.loadProtocolContext();
  },
  beforeDestroy() {
    this.stopProtocolReading();
    document.removeEventListener("visibilitychange", this.onProtocolVisibilityChange);
  },
  methods: {
    startProtocolReading() {
      this.stopProtocolReading();
      this.checkProtocolBottom();
      if (!this.protocolReadRequired) return;
      this._protocolReadTick = Date.now();
      document.addEventListener("visibilitychange", this.onProtocolVisibilityChange);
      this._protocolReadTimer = setInterval(this.updateProtocolReading, 250);
    },
    updateProtocolReading() {
      const now = Date.now();
      if (this.protocolDialogVisible && this.protocolReadRequired && !document.hidden) {
        this._protocolReadElapsed = Math.min(30000, (this._protocolReadElapsed || 0) + Math.max(0, Math.min(1000, now - this._protocolReadTick)));
        this.protocolReadSeconds = Math.floor(this._protocolReadElapsed / 1000);
      }
      this._protocolReadTick = now;
    },
    onProtocolVisibilityChange() {
      this._protocolReadTick = Date.now();
    },
    stopProtocolReading() {
      clearInterval(this._protocolReadTimer);
      this._protocolReadTimer = null;
    },
    checkProtocolBottom() {
      const content = this.$refs.protocolContent;
      if (!this.protocolDialogVisible || !content || !content.clientHeight) return;
      if ([...content.querySelectorAll("img")].some(image => !image.complete)) return;
      if (content.scrollHeight - content.scrollTop - content.clientHeight <= 5) this.protocolReadBottom = true;
    },
    resetProtocolReading() {
      this.stopProtocolReading();
      this._protocolReadElapsed = 0;
      this.protocolReadSeconds = 0;
      this.protocolReadBottom = false;
      this.protocolReadExempt = false;
      this.agreed = false;
    },
    attachPathOf(signature) {
      try {
        return signature && signature.signAttach ? JSON.parse(signature.signAttach).path : "";
      } catch (e) {
        return "";
      }
    },
    formatTime(time) {
      return time ? this.$moment(time).format("YYYY-MM-DD HH:mm:ss") : "";
    },
    // 查看协议：历史申请展示申请留存正文；新申请展示当前 sign-context 正文
    async openProtocol() {
      if (this.isHistoryView) {
        if (!this.historySignature) {
          this.$message.error("历史申请未保存当次签名，无法查看协议");
          return;
        }
        this.protocolDialogVisible = true;
        return;
      }
      if (!this.embedded && this.isViewOther) {
        this.$message.error("历史申请未保存当次签名，无法查看协议");
        return;
      }
      if (!this.protocol.protocolContent && !this.contextReady) {
        await this.loadProtocolContext();
      }
      if (!this.protocol.protocolContent) {
        this.$message.error("协议内容暂不可用，请稍后重试");
        return;
      }
      this.protocolDialogVisible = true;
    },
    // 加载当前协议上下文（ms-configure sign-context），失败禁止签署
    async loadProtocolContext() {
      const requestId = ++this.contextRequestId;
      this.contextReady = false;
      this.contextError = false;
      const owner = this.embedded ? signatureOwner() : null;
      if (this.embedded && !owner) {
        this.contextError = true;
        return;
      }
      try {
        const res = await this.api.get(
          `/${serverConfig.msConfigureService}/protocol/sign-context/PLATFORM_CONFIDENTIAL`
        );
        if (requestId !== this.contextRequestId) return;
        if (owner && !sameSignatureOwner(owner, signatureOwner())) {
          this.blockForChangedSignatureOwner();
          return;
        }
        const context = res && res.data && res.data.data;
        if (!res || !res.data || res.data.code != serverConfig.REQUIRE_SUCCESS_CODE || !context
            || !context.protocolGuid || !context.protocolContent || !context.contentDigest) {
          this.contextError = true;
          this.$message.error("平台保密协议查询失败，请稍后重试");
          return;
        }
        if (this.protocol.protocolGuid !== context.protocolGuid
            || this.protocol.contentDigest !== context.contentDigest
            || String(this.protocol.protocolVersion || "1") !== String(context.protocolVersion || "1")) this.resetProtocolReading();
        this.protocol = {
          ...context,
          // 旧口径：版本为空按 "1" 展示/签署
          protocolVersion: context.protocolVersion || "1",
        };
        this.contextReady = true;
      } catch (error) {
        if (requestId !== this.contextRequestId) return;
        if (owner && !sameSignatureOwner(owner, signatureOwner())) this.blockForChangedSignatureOwner();
        else {
          this.contextError = true;
          this.$message.error("平台保密协议查询失败，请稍后重试");
        }
      }
    },
    // 历史申请：读取该申请自己的快照，legacy 只展示签名、缺失正文如实提示
    async loadHistorySignature() {
      const requestId = ++this.historyRequestId;
      this.historyLoading = true;
      this.historyError = false;
      this.historySignature = null;
      const owner = signatureOwner();
      const certGuid = encodeURIComponent(this.$route.query.certGuid || "");
      const tenantGuid = encodeURIComponent(localStorage.guid || "");
      try {
        const res = await this.api.get(
          `/${serverConfig.msPersonnelService}/staff-cert/detail/${certGuid}?tenantGuid=${tenantGuid}`
        );
        if (requestId !== this.historyRequestId) return;
        if (owner && !sameSignatureOwner(owner, signatureOwner())) {
          this.blockForChangedSignatureOwner();
          return;
        }
        const detail = res && res.data && res.data.data;
        const queryStaffGuid = this.$route.query.staffGuid || "";
        if (!res || !res.data || res.data.code != serverConfig.REQUIRE_SUCCESS_CODE || !detail
            || (queryStaffGuid && detail.staffGuid && detail.staffGuid !== queryStaffGuid)) {
          this.historyError = true;
          return;
        }
        const signature = parseCertSignature(detail);
        if (signature && signature.corrupted) {
          this.historyError = true;
          return;
        }
        this.historySignature = signature;
      } catch (error) {
        if (requestId !== this.historyRequestId) return;
        if (owner && !sameSignatureOwner(owner, signatureOwner())) this.blockForChangedSignatureOwner();
        else this.historyError = true;
      } finally {
        if (requestId === this.historyRequestId) this.historyLoading = false;
      }
    },
    initCanvas() {
      // 拦截/查看他人等场景画布不渲染
      const canvas = this.$refs.signCanvas;
      if (!canvas) return;
      this.ctx = canvas.getContext("2d");
      this.painting = false;
      this.hasDrawn = false;
      this.undoStack = [];
      this.ctx.strokeStyle = "#333";
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = "round";
    },
    startDraw(e) {
      if (!this.canSign || this.submitting || !this.ctx) return;
      // 撤销栈：记录落笔前画面（=上一笔完成时），供"撤销"逐步回退
      const canvas = this.$refs.signCanvas;
      this.undoStack.push(this.ctx.getImageData(0, 0, canvas.width, canvas.height));
      this.painting = true;
      this.hasDrawn = true;
      this.ctx.beginPath();
      const point = this.canvasPoint(e);
      this.ctx.moveTo(point.x, point.y);
    },
    drawing(e) {
      if (!this.painting) return;
      const point = this.canvasPoint(e);
      this.ctx.lineTo(point.x, point.y);
      this.ctx.stroke();
    },
    canvasPoint(e) {
      const canvas = this.$refs.signCanvas;
      const rect = canvas.getBoundingClientRect ? canvas.getBoundingClientRect() : null;
      return {
        x: e.offsetX * (rect && rect.width ? canvas.width / rect.width : 1),
        y: e.offsetY * (rect && rect.height ? canvas.height / rect.height : 1),
      };
    },
    endDraw() {
      this.painting = false;
    },
    // 撤销上一笔（含清除前的快照，可连续撤销到空白）
    undoDraw() {
      const canvas = this.$refs.signCanvas;
      const snapshot = this.undoStack.pop();
      if (!snapshot) return;
      this.ctx.putImageData(snapshot, 0, 0);
      this.hasDrawn = this.undoStack.length > 0 || this.hasCanvasContent();
    },
    // 画布是否有笔迹（撤销到底时判断签名是否仍存在）
    hasCanvasContent() {
      const canvas = this.$refs.signCanvas;
      const data = this.ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return true;
      }
      return false;
    },
    clearCanvas() {
      const canvas = this.$refs.signCanvas;
      // 清除前留快照，同样可撤销恢复
      this.undoStack.push(this.ctx.getImageData(0, 0, canvas.width, canvas.height));
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.hasDrawn = false;
    },
    blockForChangedSignatureOwner() {
      this.resetProtocolReading();
      this.protocolDialogVisible = false;
      this.submitting = false;
      this.editingSignature = false;
      this.hasDrawn = false;
      this.undoStack = [];
      this.contextRequestId += 1;
      this.historyRequestId += 1;
      this.historyLoading = false;
      this.historyError = false;
      this.historySignature = null;
      this.signatureOwnerChanged = true;
      this.$message.error("当前人员或企业已变化，请重新进入认证页面");
    },
    startModifySignature() {
      if (!this.canModifySignature) return;
      this.editingSignature = true;
      this.hasDrawn = false;
      this.$nextTick(() => this.initCanvas());
    },
    cancelModifySignature() {
      if (this.submitting) return;
      this.editingSignature = false;
      this.hasDrawn = false;
      this.undoStack = [];
    },
    exportCanvas() {
      return new Promise((resolve) => {
        try {
          this.$refs.signCanvas.toBlob((blob) => resolve(blob), "image/png");
        } catch (error) {
          resolve(null);
        }
      });
    },
    // 确认签名：画布导出→OBS 上传→emit 新签对象供父表单保存，不调用任何协议写接口
    async onSign() {
      if (!this.canSign || this.submitting || this.confirmingConsent) return;
      if (!this.hasDrawn) {
        this.$message.error("请手写签名后再签署");
        return;
      }
      if (!this.protocolReadReady) {
        await this.openProtocol();
        return;
      }
      const owner = this.embedded ? signatureOwner() : null;
      if (this.embedded && !owner) {
        this.$message.error("无法确认当前人员和企业，请重新进入认证页面");
        return;
      }
      if (this.embedded && !this.signaturePresent && !this.agreed) {
        const digest = this.protocol.contentDigest;
        this.confirmingConsent = true;
        try {
          await this.$confirm("我已阅读并同意《平台保密协议》，确认以上为本人签名。", "平台保密协议", {
            confirmButtonText: "同意", cancelButtonText: "取消", closeOnClickModal: false,
          });
        } catch (error) {
          return;
        } finally {
          this.confirmingConsent = false;
        }
        if (!sameSignatureOwner(owner, signatureOwner())) {
          this.blockForChangedSignatureOwner();
          return;
        }
        if (!this.canSign || this.submitting || !this.hasDrawn || !this.protocolReadReady || this.protocol.contentDigest !== digest) return;
        this.agreed = true;
      }
      this.submitting = true;
      try {
        const blob = await this.exportCanvas();
        if (owner && !sameSignatureOwner(owner, signatureOwner())) {
          this.blockForChangedSignatureOwner();
          return;
        }
        if (!blob) {
          this.$message.error("签名图片生成失败，请重试");
          return;
        }
        const fileName = signatureFileName();
        const formData = new FormData();
        formData.append("file", blob, fileName);
        formData.append("fileName", fileName);
        const res = await this.api.postImage(
          `/${serverConfig.msCommonAdminCloudService}/obs/upload-file-include-filename`,
          formData
        );
        if (owner && !sameSignatureOwner(owner, signatureOwner())) {
          this.blockForChangedSignatureOwner();
          return;
        }
        if (!res || !res.data || res.data.code != serverConfig.REQUIRE_SUCCESS_CODE
            || !res.data.data || !res.data.data.data) {
          this.$message.error((res && res.data && res.data.msg) || "签名上传失败");
          return;
        }
        this.$emit("signature-change", {
          signAttach: JSON.stringify({ fileName, path: res.data.data.data }),
          signTime: Date.now(),
          protocolGuid: this.protocol.protocolGuid,
          protocolVersion: this.protocol.protocolVersion || "1",
          contentDigest: this.protocol.contentDigest,
        });
        this.editingSignature = false;
        this.protocolReadExempt = true;
        this.hasDrawn = false;
        this.undoStack = [];
        this.$message.success("签名已确认，保存或提交后生效");
      } catch (error) {
        if (owner && !sameSignatureOwner(owner, signatureOwner())) this.blockForChangedSignatureOwner();
        else this.$message.error("签名上传失败，请重试");
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      // FOUR-2975 终版：返回按钮恒定跳认证资质申请页（不再区分站内原路返回/待办兜底/审批人视角）；
      // 带 productGuid 时保留，维持待办上下文
      const productGuid = this.$route.query.productGuid;
      this.$router.push(productGuid ? "/certInfoManage?productGuid=" + productGuid : "/certInfoManage");
    },
  },
};
</script>

<style scoped>
/* 标题条（布局抄 components/title/title.vue：返回在左，标题居中） */
.title-box {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-top: 1px solid #e5e5e5;
}
.page-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 28px;
  font-size: 12px;
  line-height: 28px;
  color: #212121;
  background: #ffffff;
  border-radius: 2px;
  cursor: pointer;
  margin-left: 16px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
}
.page-title {
  flex: 1;
  font-size: 16px;
  text-align: center;
}
.protocol-sign {
  padding: 16px 24px;
  background: #fff;
  min-height: 100%;
  /* #app 全局 text-align:center 会波及，签署信息等内容显式靠左 */
  text-align: left;
}
/* 可折叠卡片区块（questionnaireAdd 同款框架） */
.base {
  background: #fff;
  border: 1px solid #d9d9d9;
  margin-bottom: 12px;
  text-align: left;
}
.base > .title {
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  border-bottom: 1px solid #ebeef5;
  background-color: #fafafa;
  cursor: pointer;
}
.title-config i {
  margin-right: 8px;
  color: #606266;
}
.sign-body {
  padding: 12px 16px;
}
.protocol-content {
  padding: 16px;
  max-height: 50vh;
  overflow-y: auto;
  line-height: 1.8;
}
.full-protocol-content {
  max-height: 65vh;
  overflow-y: auto;
  line-height: 1.8;
  text-align: left;
}
.protocol-link {
  color: #4fa1a4;
  cursor: pointer;
}
.protocol-dialog-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 30px;
}
.protocol-fullscreen-button {
  right: 52px;
}
.protocol-reading-dialog.is-fullscreen .full-protocol-content {
  max-height: calc(100vh - 180px);
}
.sign-label {
  margin-bottom: 8px;
  color: #606266;
}
.canvas-wrap {
  position: relative;
  display: inline-block;
}
.canvas-wrap canvas {
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  cursor: crosshair;
  background: #fafafa;
}
.canvas-btns {
  position: absolute;
  right: 8px;
  top: 8px;
}
.signed-sign-box {
  margin-top: 12px;
}
.signed-sign-img {
  max-width: 600px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
}
.sign-viewable {
  cursor: zoom-in;
}
.sign-date {
  margin-top: 8px;
  color: #606266;
}
.signed-tip {
  color: #3f9a94;
  padding: 12px;
  background: #f0f7f6;
  border-radius: 4px;
}
.blocked-tip {
  color: #e6a23c;
  padding: 12px;
  background: #fdf6ec;
  border-radius: 4px;
}
.btn-bar {
  margin-top: 24px;
  text-align: center;
}
.is-embedded .protocol-sign {
  display: block;
  padding: 0 16px 16px;
}
.is-embedded .protocol-sign .base {
  min-width: 0;
  margin: 0;
  border: 0;
}
.is-embedded .protocol-sign .title {
  padding: 12px 0 8px;
  border: 0;
}
.is-embedded .protocol-agreement {
  margin-bottom: 14px;
}
.is-embedded .sign-body {
  padding: 0;
}
.is-embedded .canvas-wrap {
  display: block;
  width: 100%;
  max-width: 600px;
}
.is-embedded .canvas-wrap canvas {
  display: block;
  width: 100%;
  max-width: 600px;
  height: auto;
}
.is-embedded .signed-sign-img {
  max-width: 100%;
}
.is-embedded .btn-bar {
  margin-top: 8px;
  text-align: left;
}
</style>

<style>
/* 签名预览弹窗（append-to-body 挂 body 下，scoped 管不到；类名唯一不误伤他页） */
.sign-preview-dialog .sign-preview-img {
  display: block;
  max-width: 100%;
  margin: 0 auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #ffffff;
}
</style>
